import { useEffect, useState } from 'react';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment-timezone';

import { IFilterItem } from './SalesInsights';
import CustomDateRangePicker from '../../components/DateRangePicker';
import { adjustDateRange, getDefaultDates } from './utils';
import { PickersShortcutsItem } from '@mui/x-date-pickers-pro';

const getDefaultDateRanges = (options?: { maxDate?: Dayjs }): PickersShortcutsItem<DateRange<Dayjs>>[] => {
    const { maxDate: defaultMaxDate } = options || {};

    const today = dayjs().day(0).startOf('day');
    const maxDate = defaultMaxDate ? dayjs(defaultMaxDate) : today.endOf('day');

    return [
        {
            label: 'Previous Week',
            getValue: () => {
                return [today.subtract(1, 'week'), maxDate];
            },
        },
        {
            label: 'Last 2 Weeks',
            getValue: () => {
                return [today.subtract(2, 'week'), maxDate];
            },
        },
        {
            label: 'Last 4 Weeks',
            getValue: () => {
                return [today.subtract(4, 'week'), maxDate];
            },
        },
        {
            label: 'Last 8 Weeks',
            getValue: () => {
                return [today.subtract(8, 'week'), maxDate];
            },
        },
        {
            label: 'Last 12 Weeks',
            getValue: () => {
                return [today.subtract(12, 'week'), maxDate];
            },
        },
    ];
};

const SelectMonth = ({
    id,
    setFilters,
    filters,
}: {
    id: number;
    setFilters: (filters: IFilterItem[]) => void;
    filters: IFilterItem[];
}) => {
    const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultDates();

    const today = dayjs();
    const sunday = today.day(0);
    const newDefaultMinDate = today.subtract(1, 'year');
    const [minDate] = useState(newDefaultMinDate);
    const [maxDate] = useState(sunday);
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);

    const handleDateRangeChange = (newDateRange: any): void => {
        if (newDateRange[0] && newDateRange[1]) {
            const newStartDate: Date = newDateRange[0].toDate();
            const newEndDate: Date = newDateRange[1].toDate();

            const dateRange = adjustDateRange(newStartDate, newEndDate);

            const newFilters = [...filters];
            newFilters[id]?.children?.splice(0, newFilters[id]?.children?.length);
            newFilters[id]?.children?.push({
                id:
                    moment(dateRange.startDate).format('YYYY-MM-DD') +
                    '@' +
                    moment(dateRange.endDate).format('YYYY-MM-DD'),

                label:
                    moment(dateRange.startDate).format('MMMM DD YYYY') +
                    '-' +
                    moment(dateRange.endDate).format('MMMM DD YYYY'),
            });
            setStartDate(newStartDate);
            setEndDate(newEndDate);
            setFilters(newFilters);
        }
    };

    useEffect(() => {
        if (filters?.[id]?.children?.length === 1) {
            const ids = filters?.[id]?.children?.[0]?.id?.split('@');
            if (ids?.length === 2) {
                const newStartDate = new Date(ids[0]);
                const newEndDate = new Date(ids[1]);
                setStartDate(newStartDate);
                setEndDate(newEndDate);
            }
        }
    }, [filters, id, maxDate]);

    const isTuedayToSaturday = (date: Dayjs) => {
        const day = date.day();

        return day > 1;
    };

    return (
        <CustomDateRangePicker
            label="Date Range"
            ranges={getDefaultDateRanges({ maxDate })}
            minDate={dayjs(minDate)}
            maxDate={dayjs(maxDate)}
            disableFuture={true}
            value={[dayjs(startDate), dayjs(endDate)]}
            onChange={handleDateRangeChange}
            onSelect={handleDateRangeChange}
            showToolbar={false}
            shouldDisableDate={isTuedayToSaturday}
            toolbarText={'You can only select one week date intervals from Monday to Sunday'}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        />
    );
};

export default SelectMonth;
